import { Icon } from '@resolution-processing/balance';
import type { ContactSummary } from '../api.ts';
import styles from './StatusBanner.module.css';

interface StatusBannerProps {
  contactId: string;
  contactSummary?: ContactSummary;
}

type IconName = Parameters<typeof Icon>[0]['name'];

function formatDateTime(dateStr: string): string {
  // Forth returns MySQL datetime format (space-separated); normalize to ISO 8601
  const d = new Date(dateStr.replace(' ', 'T'));
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDate(dateStr: string): string {
  // Date-only strings (YYYY-MM-DD) are parsed as UTC midnight, which causes
  // off-by-one in US timezones. Parse the parts directly instead.
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
}

function Field({ label, value, icon }: { label: string; value: string; icon: IconName }) {
  return (
    <div className={styles.field}>
      <div className={styles.fieldHeader}>
        <Icon name={icon} width={11} height={11} className={styles.fieldIcon} />
        <div className={styles.fieldLabel}>{label}</div>
      </div>
      <div className={styles.fieldValue}>{value}</div>
    </div>
  );
}

export function StatusBanner({ contactId, contactSummary }: StatusBannerProps) {
  return (
    <div className={styles.card}>
      <div className={styles.nameRow}>
        <div className={styles.name}>
          {contactSummary
            ? `${contactSummary.firstName} ${contactSummary.lastName}`
            : contactId}
        </div>
        {(contactSummary?.statusLabel || contactSummary?.stageLabel) && (
          <div className={styles.badges}>
            {contactSummary?.statusLabel && (
              <span className={styles.badge}>
                <span className={styles.badgeType}>Status</span>
                {contactSummary.statusLabel}
              </span>
            )}
            {contactSummary?.stageLabel && (
              <span className={styles.badge}>
                <span className={styles.badgeType}>Stage</span>
                {contactSummary.stageLabel}
              </span>
            )}
          </div>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>Contact Info</div>
        <div className={styles.fields}>
          <Field label="ID" value={contactId} icon="vcard" />
          {contactSummary?.state && <Field label="State" value={contactSummary.state} icon="pin" />}
          {contactSummary?.email && <Field label="Email" value={contactSummary.email} icon="email-closed" />}
          {contactSummary?.phoneCell && <Field label="Cell" value={contactSummary.phoneCell} icon="phone-one" />}
          {contactSummary?.phoneHome && <Field label="Home" value={contactSummary.phoneHome} icon="phone-one" />}
        </div>
      </div>

      {contactSummary && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>Enrollment Info</div>
          <div className={styles.fields}>
            <Field label="Total Debt" value={`$${parseFloat(contactSummary.totalDebt).toLocaleString()}`} icon="money" />
            {contactSummary.submittedAt && (
              <Field label="Submitted" value={formatDateTime(contactSummary.submittedAt)} icon="calendar" />
            )}
            {contactSummary.firstPaymentDate && (
              <Field label="First Payment" value={formatDate(contactSummary.firstPaymentDate)} icon="payment-plans" />
            )}
            {(contactSummary.servicingCompany || contactSummary.assignedCompany || contactSummary.marketingAgent) && (
              <div className={styles.fieldBreak} />
            )}
            {contactSummary.servicingCompany && (
              <Field label="Servicing" value={contactSummary.servicingCompany} icon="billing" />
            )}
            {contactSummary.assignedCompany && (
              <Field label="Assigned Co" value={contactSummary.assignedCompany} icon="user-multiple" />
            )}
            {contactSummary.marketingAgent && (
              <Field label="Agent" value={contactSummary.marketingAgent} icon="user-shape-circle" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
